/**
 * Which build the browser is *actually running*, read from the document itself.
 *
 * The service worker answers every navigation from the precached `index.html`
 * (see `src/service-worker.ts`), so a visitor can load a build that is no longer
 * the deployed one. Nothing in the bundle knows its own identity — the release
 * manifest carries a commit sha, and the asset manifest carries hashed file
 * paths, and the two cannot be compared. What the two do share is the main entry
 * script: the deployed manifest names it, and the running document has loaded
 * it. Comparing those two answers "am I stale?" without any build-time plumbing.
 */

/** The main entry chunk CRA emits, hashed per build. */
const MAIN_SCRIPT_PATTERN = /\/static\/js\/main\.[0-9a-zA-Z]+\.js$/;

/** The key CRA's asset manifest files the main entry chunk under. */
const MANIFEST_MAIN_SCRIPT_KEY = "main.js";

/**
 * Reduce a script reference to its path, so a manifest entry and a DOM `src`
 * compare equal despite one being relative and the other absolute.
 *
 * @param reference - A script path or URL, from either the manifest or the DOM
 * @returns The pathname, or null when the reference cannot be parsed
 */
const readScriptPath = (reference: string): string | null => {
  try {
    return new URL(reference, window.location.origin).pathname;
  } catch {
    return null;
  }
};

/**
 * The main entry script this document actually loaded.
 *
 * Returns null wherever the running build cannot be identified — the dev server
 * emits an unhashed bundle, and a test renders no script tags at all. Callers
 * treat null as "cannot tell" rather than "unchanged", so an unidentifiable
 * build never produces a false update prompt.
 *
 * Output shape: `"/static/js/main.9f3a1c2d.js"` or `null`.
 *
 * @returns The pathname of the loaded main chunk, or null when there is none
 */
export const resolveLoadedMainScriptPath = (): string | null => {
  if (typeof document === "undefined") {
    return null;
  }

  const scripts: HTMLScriptElement[] = Array.from(document.querySelectorAll("script[src]"));
  for (const script of scripts) {
    const path = readScriptPath(script.src);
    if (path !== null && MAIN_SCRIPT_PATTERN.test(path)) {
      return path;
    }
  }

  return null;
};

/**
 * The main entry script the deployed asset manifest names.
 *
 * @param files - The `files` map from `/asset-manifest.json`
 * @returns The pathname of the deployed main chunk, or null when absent
 */
export const resolveDeployedMainScriptPath = (files: Record<string, string>): string | null => {
  const reference = files?.[MANIFEST_MAIN_SCRIPT_KEY];
  if (typeof reference !== "string" || reference === "") {
    return null;
  }

  const path = readScriptPath(reference);
  return path !== null && MAIN_SCRIPT_PATTERN.test(path) ? path : null;
};
